import { WithAuthorization } from "../../../context";
import { useDispatch } from "react-redux";
import {
  Button,
  Center,
  chakra,
  Divider,
  Flex,
  Heading,
  Tab,
  TabList, TabPanel,
  TabPanels,
  Tabs,
  useToast
} from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  reportKeys,
  useExportReport,
  useGetReport,
} from "../../../hooks/query/reports";
import { useForm } from "react-hook-form";
import {
  calculateDefaultValues,
  generateArrayOfYears,
  showErrorToast,
  translateSubElementString
} from "../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { Page } from "../../../components/Page";
import { Card } from "../../../components/Card";
import { FormSelect } from "../../../components/Forms";
import { MissingDataLabel } from "../components/MissingDataLabel";
import { useGetOrganizationStructure } from "../../../hooks/query";
import { ScopeOneAndTwoReport } from "./components/ScopeOneAndTwoReport";
import { ScopeThree } from "./components/ScopeThree";
import { ByDimensionReport } from "./components/ByDimensionReport";
import { ConsumptionsData } from "./components/ConsumptionsData";

function EmissionsReports() {
  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [mainTabIndex, setMainTabIndex] = useState(0);

  const { t } = useTranslation("translation", {
    keyPrefix: "reportsPage"
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: reportKeys.reportData() });
  }, [queryClient]);

  const {
    data: organizationStructure
  } = useGetOrganizationStructure({
    refetchOnWindowFocus: false
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: calculateDefaultValues()
  });

  const watchYear = watch("financialYear");
  const watchQuarter = watch("quarter");

  const handleErrors = (err) => {
    if (err.response?.status === 404) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const {
    data: reportData,
    isFetching,
    error: reportError
  } = useGetReport({
    year: watchYear,
    quarter: watchQuarter
  }, {
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const {
    mutate: exportReport
  } = useExportReport({
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  useEffect(() => {
    if (isFetching) {
      dispatch(showAppSpinner());
    }
  }, [isFetching, dispatch]);

  const onSubmit = (data) => {
    dispatch(showAppSpinner());
    exportReport({ params: { year: data.financialYear, quarter: data.quarter } });
  };

  return (
    <Page title={t("emissionsReports.page.title")}
          subTitle={translateSubElementString("reportsPage.emissionsReports.page.subTitle")}>
      <Card>
        <Heading size="md" marginBlock="1">
          {t("selectPeriod")}
        </Heading>
        <chakra.form id="calculateReport" onSubmit={handleSubmit(onSubmit)}>
          <Flex gap="4" marginBlockStart="5" alignItems="end" justifyContent="space-between">
            <Flex width="50%" gap="4">
              <FormSelect
                label={t("inputs.financialYear")}
                errors={errors}
                {...register("financialYear", { required: true })}
              >
                {generateArrayOfYears().map((year) => <option key={year} value={year}>{year}</option>)}
              </FormSelect>
              <FormSelect
                label={t("inputs.quarter")}
                errors={errors}
                {...register("quarter", { required: true })}
              >
                <option key="ALL" value="ALL">ALL</option>
                <option key="Q1" value="Q1">Q1</option>
                <option key="Q2" value="Q2">Q2</option>
                <option key="Q3" value="Q3">Q3</option>
                <option key="Q4" value="Q4">Q4</option>
              </FormSelect>
            </Flex>
            <Button
              colorScheme="customScheme"
              width="32"
              type="submit"
              isDisabled={reportError?.response?.status === 404}
              title={reportError?.response?.status === 404 ?
                t("missingDataError") : t("buttons.globalExportTooltip")}
            >
              {t("buttons.export")}
            </Button>
          </Flex>
        </chakra.form>
      </Card>
      <Divider marginBlock="5" />
      <Card>
        <Tabs colorScheme="customScheme" index={mainTabIndex} onChange={setMainTabIndex} overflow="auto">
          <Center>
            <TabList borderBottom="none" overflow="auto">
              <Tab mb={0} _focus={{ boxShadow: "none" }}>{t("tabs.scopeOneAndTwo")}</Tab>
              <Tab mb={0} _focus={{ boxShadow: "none" }}>{t("tabs.scopeThree")}</Tab>
              <Tab mb={0} _focus={{ boxShadow: "none" }}>{t("tabs.byDimension")}</Tab>
              <Tab mb={0} _focus={{ boxShadow: "none" }}>{t("tabs.consumptionsData")}</Tab>
            </TabList>
          </Center>
          <TabPanels>
            <TabPanel padding="0">
              {!isFetching && !reportError && reportData && reportData.id && reportData.scopeOneAndTwoTotal &&
                reportData.scopeThree &&
                <ScopeOneAndTwoReport
                  reportId={reportData.id}
                  data={reportData.scopeOneAndTwoTotal}
                  manualEmissions={reportData.manualEmissions}
                  year={watchYear}
                  quarter={watchQuarter}
                  scopeThreeTotalCo2Emissions={reportData.scopeThree.totalCo2Emissions}
                />
              }
              {(!reportData || !reportData.id || !reportData.scopeOneAndTwoTotal || reportError) && <MissingDataLabel />}
            </TabPanel>
            <TabPanel padding="0">
              {!isFetching && reportData && reportData.scopeThree && !reportError &&
                <ScopeThree data={reportData.scopeThree} reportId={reportData.id} manualEmissions={reportData.manualEmissions} />
              }
              {(!reportData || !reportData.scopeThree || reportError) && <MissingDataLabel />}
            </TabPanel>
            <TabPanel padding="0">
              {!isFetching && reportData && !reportError && organizationStructure &&
                <ByDimensionReport
                  allReportsData={reportData}
                  locationElectricityItems={reportData.locationElectricityItems}
                  organizationStructure={organizationStructure}
                />
              }
              {(!reportData || reportError) && <MissingDataLabel />}
            </TabPanel>
            <TabPanel padding="0">
              <ConsumptionsData organizationStructure={organizationStructure} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Card>
    </Page>
  );
}

export default WithAuthorization(EmissionsReports);